import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdditionalActivitiesWidget } from '../ui/diary/AdditionalActivitiesWidget';
import type {
  AdditionalActivitiesWidgetData,
  AdditionalActivitiesWidgetLabels,
} from '../shared/types/additional-activities';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

export interface AdditionalActivitiesWidgetContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  diaryDayId: string;
  labels: AdditionalActivitiesWidgetLabels;
}

const GET_WIDGET_QUERY = `
  query GetAdditionalActivitiesWidget($diaryDayId: ID!) {
    additionalActivitiesWidget(diaryDayId: $diaryDayId) {
      diaryDayId
      activities {
        id
        name
        createdAt
      }
      suggestions
      manageUrl
    }
  }
`;

const ADD_ACTIVITY_MUTATION = `
  mutation AddAdditionalActivity($diaryDayId: ID!, $name: String!) {
    addAdditionalActivity(input: { diaryDayId: $diaryDayId, name: $name }) {
      success
      activity {
        id
        name
        createdAt
      }
    }
  }
`;

const REMOVE_ACTIVITY_MUTATION = `
  mutation RemoveAdditionalActivity($diaryDayId: ID!, $activityId: ID!) {
    removeAdditionalActivity(input: { diaryDayId: $diaryDayId, activityId: $activityId }) {
      success
    }
  }
`;

function AdditionalActivitiesWidgetInner({
  graphqlEndpoint,
  csrfToken,
  diaryDayId,
  labels,
}: AdditionalActivitiesWidgetContainerProps) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['additionalActivitiesWidget', diaryDayId],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_WIDGET_QUERY,
          variables: { diaryDayId },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.additionalActivitiesWidget as AdditionalActivitiesWidgetData;
    },
  });

  const addActivityMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: ADD_ACTIVITY_MUTATION,
          variables: { diaryDayId, name },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add activity');
      }

      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additionalActivitiesWidget', diaryDayId] });
    },
  });

  const removeActivityMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: REMOVE_ACTIVITY_MUTATION,
          variables: { diaryDayId, activityId },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove activity');
      }

      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additionalActivitiesWidget', diaryDayId] });
    },
  });

  const handleAddActivity = useCallback(
    (name: string) => {
      addActivityMutation.mutate(name);
    },
    [addActivityMutation]
  );

  const handleRemoveActivity = useCallback(
    (activityId: string) => {
      removeActivityMutation.mutate(activityId);
    },
    [removeActivityMutation]
  );

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <AdditionalActivitiesWidget
      activities={data.activities}
      suggestions={data.suggestions}
      manageUrl={data.manageUrl}
      onAddActivity={handleAddActivity}
      onRemoveActivity={handleRemoveActivity}
      isAdding={addActivityMutation.isPending}
      labels={labels}
    />
  );
}

export function AdditionalActivitiesWidgetContainer(props: AdditionalActivitiesWidgetContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AdditionalActivitiesWidgetInner {...props} />
    </QueryClientProvider>
  );
}

export function mountAdditionalActivitiesWidget(
  container: HTMLElement,
  props: AdditionalActivitiesWidgetContainerProps
) {
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<AdditionalActivitiesWidgetContainer {...props} />);
  return root;
}

if (typeof window !== 'undefined') {
  (window as any).mountAdditionalActivitiesWidget = mountAdditionalActivitiesWidget;
}

export default AdditionalActivitiesWidgetContainer;
